// Booking sales tax: which rate applies, and how many cents it adds.
//
// A booking is sourced at a dealer LOCATION (origin-based), not at the
// customer's address, so the rate comes from the location — falling back to
// the dealer's default — and only when the dealer has sales tax switched on.
// The amount is added ON TOP of the booking price and stored in its own
// columns; it is never folded into price_snapshot.
//
// The arithmetic is computeTax's, so a booking and a cart can never round the
// same number differently.
//
// CANONICAL COPY. Inlined into apps/portal/api/_lib/bookingTax.ts (Vercel bundle
// constraint — see .claude/rules/inlined-utilities.md). Keep the inlined body
// identical apart from its header, import path and export trims; the parity test
// in apps/portal/tests/unit/bookingTaxInline.test.ts enforces behaviour.

import { computeTax } from './salesTax'

export type SalesTaxMode = 'off' | 'flat'
export type BookingTaxTreatment = 'taxable' | 'exempt'
export type BookingTaxSource = 'none' | 'flat'

export interface BookingTax {
  taxCents: number
  /** The rate as applied, or null when nothing was calculated. */
  taxRatePct: number | null
  taxSource: BookingTaxSource
}

/** The rate a booking is taxed at, or null for "not taxed". An explicit 0 on
 *  the location means "don't tax here" — `??` keeps it from falling through to
 *  the dealer default. */
export function resolveBookingTaxRatePct(input: {
  mode: string | null | undefined
  locationRatePct: number | null | undefined
  tenantRatePct: number | null | undefined
}): number | null {
  if (input.mode !== 'flat') return null
  const rate = input.locationRatePct ?? input.tenantRatePct
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return null
  return rate
}

export function computeBookingTax(input: {
  ratePct: number | null
  taxTreatment: string | null | undefined
  subtotalCents: number | null
}): BookingTax {
  const { ratePct, subtotalCents } = input
  if (ratePct == null || subtotalCents == null || !Number.isFinite(subtotalCents) || subtotalCents <= 0) {
    return { taxCents: 0, taxRatePct: null, taxSource: 'none' }
  }
  const result = computeTax({
    destination: { country: 'US', postalCode: null },
    lines: [
      {
        id: 'booking',
        // Only an explicit 'exempt' exempts. null resolves to the general rate
        // inside computeTax, so an unknown treatment is taxed, never exempted.
        category: input.taxTreatment === 'exempt' ? 'INSTRUCTION' : null,
        amount: subtotalCents / 100,
      },
    ],
    stack: null,
    flatRatePct: ratePct,
    exemptCategories: ['INSTRUCTION'],
  })
  if (result.source !== 'flat') return { taxCents: 0, taxRatePct: null, taxSource: 'none' }
  return { taxCents: Math.round(result.total * 100), taxRatePct: ratePct, taxSource: 'flat' }
}
